import { getSelectedPage } from '../common';
import { postContentWithAction } from '../apiConnect';
import { base64Encode, decimalToHex, sha256Encode } from '../common';
import axios from "axios";
import { Mutex } from 'async-mutex';

const mutex = new Mutex();

export default class Auth {
    constructor(history, settings) {
      this.history = history;
      this.cacheSettings = settings;
    }

    login = (props = null) => {
      // Try to refresh existing token first, if found
      const token = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      if (token && refreshToken && this.isTokenExpired()) {
        this.refreshAccessToken().then(newToken => {
          if (!newToken?.accessToken) {
            console.log("Failed to refresh token, redirecting to login");
            this.prepareAuthCodeFlow().then(values => {
              localStorage.setItem('verifier', values.verifier);
              window.location.replace(values.authorizeUrl);
            });
          } else {
            const redirectTo = props?.location?.pathname;
            if (redirectTo) {
              console.log("Redirecting to: " + redirectTo);
              props.history.push(redirectTo);
            }
          }
        });
      } else {
        this.prepareAuthCodeFlow().then(values => {
          localStorage.setItem('verifier', values.verifier);
          window.location.replace(values.authorizeUrl);
        });
      }
    }

    ClearHttpContextSession = () => {
      // api -> Backend Session.Clear()
      const token = localStorage.getItem('access_token');
      if (token && !this.isTokenExpired()) {
        postContentWithAction(this.getAccessToken(), 'session', 'delete', {}, () => {
          //console.log('session deleted');
        });
      }
    };

    clearAuthStorage = () => {
      // Remove only auth-related keys so user settings (language, year, farm, etc.) are preserved across sessions.
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('id_token');
      localStorage.removeItem('token_expires_at');
      localStorage.removeItem('verifier');
    }

    logout = () => {
      this.ClearHttpContextSession();

      const idToken = localStorage.getItem('id_token');

      if (idToken) {
        const logoutUrl = `${this.cacheSettings.authority}/connect/endsession?id_token_hint=${idToken}&post_logout_redirect_uri=${this.cacheSettings.post_logout_redirect_uri}`;
        this.clearAuthStorage();
        window.location.replace(logoutUrl);        
      } else {
        this.clearAuthStorage();
        this.login();
      }
    }

    showWeatherStationData = () => {
        if (this.cacheSettings.show_weather_station_data === "true") return true;
        return false;
    }

    showForecasts = () => {
      if (this.cacheSettings.show_forecasts === "true") return true;
      return false;
    }

    showSharedFeatures = () => {
        if (this.cacheSettings.show_shared_features === "true") return true;
        return false;
    }

    getAccessToken = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        return null;
      } else {
        if (!this.isTokenExpired()) {
          return token;
        } else {
          const newToken = await this.refreshAccessToken();
          if (!newToken?.accessToken) {
            console.error("Failed to refresh token");
            return null;
          } else {
            return newToken.accessToken;
          }
        }
      }
    }

    isTokenExpired = () => {
      const tokenExpiry = localStorage.getItem('token_expires_at');
      if (!tokenExpiry) {
        return true;
      }

      const expiryBuffer = 10000; // Add 10 seconds buffer so token doesn't expire while using it
      return (Date.now() - expiryBuffer) > tokenExpiry;
    }

    isAuthenticated = () => {
      const accessToken = localStorage.getItem('access_token');
      if (accessToken && !this.isTokenExpired()) {
        return true;
      }
      return false;
    }

    postLogin = async (location) => {
      const params = new URLSearchParams(location.search);
      if (params.has('code')) {
        const verifier = localStorage.getItem('verifier');
        if (!verifier) {
          console.error("Failed to login, missing verifier");
          this.logout();
          return;
        }
        localStorage.removeItem('verifier');
        const token = await this.getAccessTokenWithAuthCode(params.get('code'), verifier);
        if (!token?.accessToken) {
          console.error("Failed to login, missing token");
          this.logout();
          return;
        }
      }

      let page = getSelectedPage();

      //transfer to main pages.
      if ((page > 1) && (!this.cacheSettings || this.cacheSettings.debug_mode !== "true"))
        page = 1;

      if (page === 1)
        this.history.push('/plots');
      else if (page === 2)
        this.history.push('/plotOperation');
      else if (page === 3)
        this.history.push('/mapViewer');
      else
        this.history.push('/');
    }

    generateCodeVerifier = () => {
      var array = new Uint32Array(56 / 2);
      window.crypto.getRandomValues(array);
      return Array.from(array, decimalToHex).join("");
    }

    generateCodeChallengeFromVerifier = async (verifier) => {
      var hashedVerifier = await sha256Encode(verifier);
      var challenge = base64Encode(hashedVerifier);
      return challenge;
    }

    prepareAuthCodeFlow = async () => {
      const verifier = this.generateCodeVerifier();
      const challenge = await this.generateCodeChallengeFromVerifier(verifier)

      const body = new URLSearchParams({
        'client_id': this.cacheSettings.client_id,
        'response_type': 'code',
        'scope': this.cacheSettings.scope,
        'redirect_uri': this.cacheSettings.redirect_uri,
        'code_challenge_method': 'S256',
        'code_challenge': challenge
      });

      return {
        authorizeUrl: `${this.cacheSettings.authority}/connect/authorize?${body.toString()}`,
        verifier: verifier
      };
    }

    getAccessTokenWithAuthCode = async (authCode, verifier) => {
      let token = {};

      const body = new URLSearchParams({
        'grant_type': 'authorization_code',
        'client_id': this.cacheSettings.client_id,
        'client_secret': this.cacheSettings.client_secret,
        'redirect_uri': this.cacheSettings.redirect_uri,
        'code': authCode,
        'code_verifier': verifier
      });

      const tokenUrl = `${this.cacheSettings.authority}/connect/token`;

      let headers = {
        "Content-Type": "application/x-www-form-urlencoded",
      };

      await axios.post(tokenUrl, body, { headers: headers })
        .then(response => {
          const isJson = response.headers['content-type']?.includes('application/json');
          const data = isJson ? response.data : null;
          if (data) {
            token.accessToken = data['access_token'];
            token.refreshToken = data['refresh_token'];
            token.idToken = data['id_token'];

            const expiresIn = data['expires_in'];
            token.expiresAt = Date.now() + (expiresIn * 1000);

            localStorage.setItem('access_token', token.accessToken);
            localStorage.setItem('refresh_token', token.refreshToken);
            localStorage.setItem('id_token', token.idToken);
            localStorage.setItem('token_expires_at', token.expiresAt);
          }
        })
        .catch(error => {
          console.error("Login error: " + JSON.stringify(error));
        });

        return token;
    }

    refreshAccessToken = async () => {

      const release = await mutex.acquire(); // Use Mutex so multiple parallel calls will not all try to refresh expired token

      try {
        const accessToken = localStorage.getItem("access_token");
        if (accessToken && !this.isTokenExpired()) {
          // Already refreshed by another call
          return { accessToken: accessToken };
        }
        console.log("Token expired, refreshing...");

        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          console.error("Failed to refresh access token, refresh token not found");
          return null;
        }

        let token = {};

        const body = new URLSearchParams({
          'grant_type': 'refresh_token',
          'client_id': this.cacheSettings.client_id,
          'client_secret': this.cacheSettings.client_secret,
          'refresh_token': refreshToken
        });

        const tokenUrl = `${this.cacheSettings.authority}/connect/token`;

        await axios.post(tokenUrl, body)
          .then(response => {
            const isJson = response.headers['content-type']?.includes('application/json');
            const data = isJson ? response.data : null;
            if (data) {
              token.accessToken = data['access_token'];
              token.refreshToken = data['refresh_token'];
              token.idToken = data['id_token'] ?? '';

              const expiresIn = data['expires_in'];
              token.expiresAt = Date.now() + (expiresIn * 1000);

              localStorage.setItem('access_token', token.accessToken);
              localStorage.setItem('refresh_token', token.refreshToken);
              localStorage.setItem('id_token', token.idToken);
              localStorage.setItem('token_expires_at', token.expiresAt);

              console.log("Token refreshed");
            }
          })
          .catch(error => {
            console.error("Failed to refresh token: " + JSON.stringify(error));
          });

        return token;
      } finally {
        release();
      }
    }
}